/**
 * A stored payload as a person reads it: labelled fields, lists of like rows as tables, nested objects as nested fields
 * — never raw JSON (client UI standard §8). The payload is whatever its writer stored, so the shape is read from the
 * values, not from a list of types. A request's action payload in the decision review (`DecisionPayload.tsx`) and a
 * debug session's input in its thread (`engine/src/services/debug-thread.ts`) read it alike; moved here from the client
 * so both use one copy (review N-8, 2026-11-01).
 */
export type PayloadCell = string;

export type PayloadField =
  | { kind: 'text'; key: string; label: string; text: string }
  | { kind: 'table'; key: string; label: string; columns: Array<{ key: string; label: string }>; rows: PayloadCell[][] }
  | { kind: 'group'; key: string; label: string; fields: PayloadField[] };

/**
 * `card` is the conversation's card for this same request: its title and explanation are the request's own title and
 * description, which the dialog already shows.
 */
const HIDDEN_KEYS = new Set(['card']);

type Primitive = string | number | boolean;

function isPrimitive(value: unknown): value is Primitive {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `bucketDefinitionId` / `bucket_definition_id` → "Bucket definition ID". */
export function fieldLabel(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (word === 'id' ? 'ID' : word));
  const text = words.join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function cellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  return isPlainObject(value) && Object.keys(value).length === 0;
}

/** Objects whose every value is a primitive (or empty): one table row each. */
function isRowList(values: unknown[]): values is Array<Record<string, unknown>> {
  return values.every(
    (value) =>
      isPlainObject(value) &&
      Object.values(value).every((cell) => cell === null || cell === undefined || isPrimitive(cell)),
  );
}

function fieldOf(key: string, label: string, value: unknown): PayloadField | null {
  if (isEmpty(value)) return null;
  if (isPrimitive(value)) return { kind: 'text', key, label, text: cellText(value) };
  if (Array.isArray(value)) {
    if (value.every(isPrimitive)) return { kind: 'text', key, label, text: value.map(cellText).join(', ') };
    if (isRowList(value)) {
      const keys = [...new Set(value.flatMap((row) => Object.keys(row)))];
      return {
        kind: 'table',
        key,
        label,
        columns: keys.map((column) => ({ key: column, label: fieldLabel(column) })),
        rows: value.map((row) => keys.map((column) => cellText(row[column]))),
      };
    }
    const fields = value
      .map((item, index) => fieldOf(`${key}.${index}`, `${index + 1}`, item))
      .filter((field): field is PayloadField => field !== null);
    return fields.length > 0 ? { kind: 'group', key, label, fields } : null;
  }
  if (isPlainObject(value)) {
    const fields = objectFields(value, key);
    return fields.length > 0 ? { kind: 'group', key, label, fields } : null;
  }
  return null;
}

function objectFields(value: Record<string, unknown>, prefix: string): PayloadField[] {
  return Object.entries(value)
    .map(([key, item]) => fieldOf(prefix ? `${prefix}.${key}` : key, fieldLabel(key), item))
    .filter((field): field is PayloadField => field !== null);
}

/** The payload's fields in the order the action wrote them; empty values and the conversation card are left out. */
export function payloadFields(payload: Record<string, unknown> | null): PayloadField[] {
  if (!payload) return [];
  const shown = Object.fromEntries(Object.entries(payload).filter(([key]) => !HIDDEN_KEYS.has(key)));
  return objectFields(shown, '');
}

/** Any value's fields: an object's by key, anything else as one field under `label`. Empty values are left out. */
export function valueFields(value: unknown, label: string): PayloadField[] {
  if (isPlainObject(value)) return objectFields(value, '');
  const field = fieldOf('value', label, value);
  return field ? [field] : [];
}
